import { useBillsList } from "@features/bx/BulkBillGeneration/hooks";
import { useDownloadBill, useViewBill } from "@features/bx/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface BillsTabProps {
  batchId: string;
}
export const BillsTab = ({ batchId }: BillsTabProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: billsData } = useBillsList(
    batchId,
    currentPage,
    PAGE_SIZE,
    searchTerm
  );

  const CURRENCY = useCurrency();

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };
  // Export handler
  const handleExport = () => {
    console.log("Exporting bills data...");
    // You can implement CSV export logic here
    const csvData = billsData?.result || [];
    console.log("Data to export:", csvData);
  };

  // Format outstanding amount with color
  const formatOutstanding = (amount: number) => {
    if (amount === 0) {
      return <span className="text-gray-900">{CURRENCY}0.00</span>;
    }
    return (
      <span className="text-orange-600 font-medium">
        {CURRENCY}
        {amount.toFixed(2)}
      </span>
    );
  };

  // Define columns for the generic table
  const columns: TableColumn<any>[] = [
    {
      key: "invoiceNo",
      header: "Invoice No",
      type: "text",
      sortable: false,
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      type: "text",
      sortable: false,
    },
    {
      key: "accountNumber",
      header: "Account Number",
      type: "text",
      sortable: false,
    },
    {
      key: "consumption",
      header: "Consumption (kWh)",
      type: "custom",
      sortable: false,
      render: (value: number) => value.toLocaleString(),
    },
    {
      key: "billAmount",
      header: "Bill Amount",
      type: "custom",
      sortable: false,
      render: (value: number) => `${CURRENCY}${value.toFixed(2)}`,
    },
    {
      key: "outstanding",
      header: "Outstanding",
      type: "custom",
      sortable: false,
      render: (value: number) => formatOutstanding(value),
    },
    {
      key: "totalDue",
      header: "Total Due",
      type: "custom",
      sortable: false,
      render: (value: number) => `${CURRENCY}${value.toFixed(2)}`,
    },
  ];

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();
  const handleDownloadClick = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const handleViewBill = (row: any) => {
    const id = row.billId || row.id;
    if (id) {
      navigate(`/bx/bill-detail/${id}`);
    }
  };

  const handleViewClick = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };
  // Define actions for the generic table
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: (row: any) => handleViewBill(row),
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Download",
      onClick: (row: any) => handleDownloadClick(row.billId || row.id),
      icon: "Download",
      variant: "outline",
    },
  ];

  // Calculate pagination info
  const totalItems = billsData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Generated Bills
        </h2>
        <p className="text-muted-foreground">
          List of bills generated in this batch
        </p>
      </div>

      {/* Table */}
      <GenericTable
        data={billsData?.result || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by invoice no, consumer name, or account number..."
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        onExport={handleExport}
        exportLoading={false}
        actions={actions}
        actionsType="icons"
        emptyMessage="No bills found"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
